import { LeagueTier, WeeklyLeagueStatus } from '../types';

export const LEAGUE_TIERS: LeagueTier[] = [
  { id: 'bronze', name: 'Bronze', minXp: 0, maxXp: 499, colorClass: 'text-amber-700' },
  { id: 'silver', name: 'Silver', minXp: 500, maxXp: 1199, colorClass: 'text-gray-300' },
  { id: 'gold', name: 'Gold', minXp: 1200, maxXp: 2499, colorClass: 'text-yellow-400' },
  { id: 'platinum', name: 'Platinum', minXp: 2500, maxXp: 3999, colorClass: 'text-neon-blue' },
  { id: 'diamond', name: 'Diamond', minXp: 4000, maxXp: 5999, colorClass: 'text-cyan-300' },
  { id: 'ascendant', name: 'Ascendant', minXp: 6000, colorClass: 'text-neon-purple' }
];

const toDateOnly = (date: Date) => date.toISOString().split('T')[0];

export const getWeekStart = (date: Date) => {
  const localDate = new Date(date);
  const day = localDate.getDay();
  const diff = (day + 6) % 7;
  localDate.setDate(localDate.getDate() - diff);
  localDate.setHours(0, 0, 0, 0);
  return localDate;
};

export const getWeekEnd = (date: Date) => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

export const getShiftedWeekRange = (weekOffset: number, now: Date = new Date()) => {
  const baseStart = getWeekStart(now);
  baseStart.setDate(baseStart.getDate() + (weekOffset * 7));
  const end = new Date(baseStart);
  end.setDate(baseStart.getDate() + 6);
  end.setHours(23, 59, 59, 999);

  return {
    start: baseStart,
    end,
    startDateOnly: toDateOnly(baseStart),
    endDateOnly: toDateOnly(end)
  };
};

export const getLeagueTier = (weeklyXp: number) => {
  const found = [...LEAGUE_TIERS].reverse().find((tier) => weeklyXp >= tier.minXp);
  return found || LEAGUE_TIERS[0];
};

export const getNextTierPreview = (weeklyXp: number) => {
  const currentTier = getLeagueTier(weeklyXp);
  const currentTierIndex = LEAGUE_TIERS.findIndex((item) => item.id === currentTier.id);
  return LEAGUE_TIERS[currentTierIndex + 1];
};

export const getProgressToNextTier = (weeklyXp: number) => {
  const currentTier = getLeagueTier(weeklyXp);
  const nextTier = getNextTierPreview(weeklyXp);

  if (!nextTier) {
    return 100;
  }

  const denominator = nextTier.minXp - currentTier.minXp;
  if (denominator <= 0) {
    return 100;
  }

  return Math.max(0, Math.min(100, Math.round(((weeklyXp - currentTier.minXp) / denominator) * 100)));
};

export const buildWeeklyStatus = (
  currentWeekXp: number,
  previousWeekXp: number,
  now: Date = new Date()
): WeeklyLeagueStatus => {
  const currentWeek = getShiftedWeekRange(0, now);
  const nextWeek = getShiftedWeekRange(1, now);
  const currentTier = getLeagueTier(previousWeekXp);
  const nextTier = getLeagueTier(currentWeekXp);

  return {
    currentTier,
    nextTier,
    currentWeekXp,
    previousWeekXp,
    weekStart: currentWeek.startDateOnly,
    weekEnd: currentWeek.endDateOnly,
    nextWeekStart: nextWeek.startDateOnly,
    nextWeekEnd: nextWeek.endDateOnly,
    progressToNext: getProgressToNextTier(currentWeekXp),
    nextTierPreview: getNextTierPreview(currentWeekXp)
  };
};
